import React from 'react';
import { Alert, Button, Spin, Typography } from 'antd';
import { PlusOutlined } from '@ant-design/icons';

import { isJobLive } from './api';
import { plural } from './format';
import { ThreadSummary } from './types';

type Props = {
  threads: ThreadSummary[] | null;
  loading: boolean;
  error: string;
  selectedId: number | null;
  onSelect: (id: number) => void;
  onNew: () => void;
  onRetry: () => void;
};

const when = (value: string) => {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? ''
    : parsed.toLocaleString([], {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      });
};

/** The chats, newest first, and the way to start another. */
const ThreadList: React.FC<Props> = ({
  threads,
  loading,
  error,
  selectedId,
  onSelect,
  onNew,
  onRetry,
}) => (
  <nav className="blueprint-builder__threads" aria-label="Chats">
    <Button
      type="primary"
      icon={<PlusOutlined />}
      block
      onClick={onNew}
      aria-pressed={selectedId == null}
    >
      New chat
    </Button>
    {error && (
      <Alert
        type="error"
        showIcon
        title={error}
        action={
          <Button size="small" onClick={onRetry}>
            Retry
          </Button>
        }
      />
    )}
    {loading && !threads ? (
      <div className="blueprint-builder__center">
        <Spin size="small" />
      </div>
    ) : threads && threads.length === 0 ? (
      <Typography.Text type="secondary" className="blueprint-builder__muted">
        No chats yet.
      </Typography.Text>
    ) : (
      <ul className="blueprint-builder__thread-list">
        {(threads || []).map(thread => (
          <li key={thread.id}>
            <button
              type="button"
              className={`blueprint-builder__thread${
                thread.id === selectedId ? ' is-selected' : ''
              }`}
              aria-current={thread.id === selectedId ? 'true' : undefined}
              onClick={() => onSelect(thread.id)}
            >
              <span className="blueprint-builder__thread-title">
                {isJobLive(thread.active_job) && (
                  <span
                    className="blueprint-builder__live-dot"
                    aria-label="working"
                  />
                )}
                {thread.title || 'Untitled chat'}
              </span>
              <span className="blueprint-builder__thread-meta">
                {plural(thread.proposal_count, 'proposal')} ·{' '}
                {when(thread.updated_at)}
              </span>
            </button>
          </li>
        ))}
      </ul>
    )}
  </nav>
);

export default ThreadList;
